import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react'
import { useMsal, useAccount } from '@azure/msal-react'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { isMsalConfigured, apiTokenRequest, loginRequest } from '../config/msalConfig'
import { authApi, setAccessToken as setApiToken, setTokenRefreshCallback } from '../services/api'

const AuthContext = createContext(null)

// Development mode mock user
const devUser = {
  sub: 'dev|local-user',
  email: 'dev@localhost',
  name: 'Development User',
  picture: 'https://via.placeholder.com/150',
  role: 'admin',
}

// Dev mode provider - doesn't use MSAL at all
export function DevAuthProvider({ children }) {
  const login = useCallback(() => {}, [])
  const logout = useCallback(() => {}, [])
  const getAccessToken = useCallback(async () => 'dev-token', [])
  
  const value = useMemo(() => ({
    isAuthenticated: true,
    isLoading: false,
    user: devUser,
    accessToken: 'dev-token',
    organizationName: 'Development Org',
    login,
    logout,
    getAccessToken,
  }), [login, logout, getAccessToken])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Production provider - uses Microsoft Entra ID via MSAL
export function MsalAuthProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal()
  const account = useAccount(accounts[0] || {})
  const [accessToken, setAccessToken] = useState(null)
  const [organizationName, setOrganizationName] = useState(null)
  const [sessionExpired, setSessionExpired] = useState(false)
  const [redirectHandled, setRedirectHandled] = useState(false)
  const [authReady, setAuthReady] = useState(false)
  const [userProfile, setUserProfile] = useState(null) // backend profile (includes role)
  const [accountBlocked, setAccountBlocked] = useState(null) // message when account is deactivated/blocked

  const isAuthenticated = accounts.length > 0
  // Keep showing the loading spinner while:
  //  - MSAL is processing a redirect/interaction
  //  - The redirect promise hasn't been handled yet
  //  - Cached accounts exist but the initial token + profile fetch hasn't completed
  //    (prevents a flash of the login page for returning users)
  const isLoading = inProgress !== 'none' || !redirectHandled || (accounts.length > 0 && !authReady)

  // Listen for session-expired events from API interceptor (401 responses)
  useEffect(() => {
    const handleSessionExpired = () => {
      console.warn('Session expired - clearing auth state')
      setAccessToken(null)
      setSessionExpired(true)
    }
    const handleAccountBlocked = (e) => {
      setAccountBlocked(e.detail?.message || 'Your account has been deactivated. Please contact your administrator.')
    }
    window.addEventListener('session-expired', handleSessionExpired)
    window.addEventListener('account-blocked', handleAccountBlocked)
    return () => {
      window.removeEventListener('session-expired', handleSessionExpired)
      window.removeEventListener('account-blocked', handleAccountBlocked)
    }
  }, [])

  const clearSessionExpired = useCallback(() => setSessionExpired(false), [])

  // Transform MSAL account to our user format, merged with backend profile
  const user = useMemo(() => {
    if (!account) return null
    return {
      sub: account.localAccountId || account.homeAccountId,
      email: account.username,
      name: account.name || account.username,
      picture: null, // Microsoft doesn't provide picture in token by default
      // Merge backend profile fields (role, id, organization_id, etc.)
      ...userProfile,
    }
  }, [account, userProfile])

  // Get access token
  const getAccessToken = useCallback(async () => {
    if (!account) return null
    
    try {
      const response = await instance.acquireTokenSilent({
        ...apiTokenRequest,
        account: account,
      })
      return response.accessToken
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Fallback to interactive method
        try {
          const response = await instance.acquireTokenPopup(apiTokenRequest)
          return response.accessToken
        } catch (popupError) {
          console.error('Error getting access token via popup:', popupError)
          return null
        }
      }
      console.error('Error getting access token:', error)
      return null
    }
  }, [instance, account])

  // Fetch token on mount and when account changes, then load user profile.
  // Gated on redirectHandled so we don't start before MSAL is ready.
  useEffect(() => {
    if (!redirectHandled) return

    const fetchTokenAndProfile = async () => {
      if (isAuthenticated && account) {
        const token = await getAccessToken()
        setAccessToken(token)

        // Register the token refresh callback so the API request interceptor
        // can proactively acquire a fresh token before expiry, and the
        // response interceptor can retry on 401.
        setTokenRefreshCallback(getAccessToken)

        // Once we have a token, fetch user profile from backend (includes subscription)
        if (token) {
          setApiToken(token)

          // Restore cached profile immediately for fast UI render
          const cached = sessionStorage.getItem('xtract_user_profile')
          if (cached) {
            try {
              const cachedData = JSON.parse(cached)
              setUserProfile({
                id: cachedData.id,
                role: cachedData.role,
                job_title: cachedData.job_title,
                photo_url: cachedData.photo_url,
                organization_id: cachedData.organization_id,
                is_active: cachedData.is_active,
              })
              if (cachedData.organization_name) setOrganizationName(cachedData.organization_name)
              // Auth is ready with cached data - don't block on /me
              setAuthReady(true)
            } catch (e) { /* ignore parse errors */ }
          }

          // Fetch fresh profile in background (updates cache)
          try {
            const res = await authApi.getCurrentUser()
            if (res.data) {
              // Store full backend profile (id, role, organization_id, etc.)
              setUserProfile({
                id: res.data.id,
                role: res.data.role,
                job_title: res.data.job_title,
                photo_url: res.data.photo_url,
                organization_id: res.data.organization_id,
                is_active: res.data.is_active,
                needs_admin: res.data.needs_admin,
              })
              // Cache for next page load
              sessionStorage.setItem('xtract_user_profile', JSON.stringify(res.data))
            }
            if (res.data?.organization_name) {
              setOrganizationName(res.data.organization_name)
            }
          } catch (err) {
            if (err.response?.status === 403 && err.response?.data?.error === 'Access Denied') {
              setAccountBlocked(err.response.data.message || 'Your account has been deactivated. Please contact your administrator.')
            } else {
              console.error('Error fetching user profile:', err)
            }
          }
        }
      }
      // Mark auth initialisation complete so isLoading becomes false
      setAuthReady(true)
    }
    fetchTokenAndProfile()
  }, [isAuthenticated, account, getAccessToken, redirectHandled])

  const handleLogin = useCallback(async () => {
    try {
      const returnUrl = localStorage.getItem('xtract_return_url') || '/'
      await instance.loginRedirect({
        ...loginRequest,
        prompt: 'select_account', // Always show the Microsoft account picker
        state: returnUrl,
      })
    } catch (error) {
      console.error('Login error:', error)
    }
  }, [instance])

  // Handle redirect promise first before attempting auto-login
  useEffect(() => {
    const handleRedirect = async () => {
      try {
        const response = await instance.handleRedirectPromise()
        if (response) {
          instance.setActiveAccount(response.account)
          // Restore the return URL from MSAL state (set during loginRedirect)
          if (response.state && response.state !== '/' && response.state !== '/login') {
            localStorage.setItem('xtract_return_url', response.state)
          }
        }
      } catch (error) {
        console.error('Error handling redirect:', error)
      } finally {
        setRedirectHandled(true)
      }
    }
    handleRedirect()
  }, [instance])

  const handleLogout = useCallback(async () => {
    try {
      // Clear local app state
      setAccessToken(null)
      setOrganizationName(null)
      setAuthReady(false)
      setApiToken(null)
      setTokenRefreshCallback(null)
      localStorage.removeItem('xtract_return_url')
      sessionStorage.removeItem('xtract_user_profile')

      // Use logoutRedirect to clear MSAL cache AND end the Azure AD session.
      // This navigates to Microsoft's logout endpoint, then redirects back.
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin + '/login',
      })
    } catch (error) {
      console.error('Logout error:', error)
      // Fallback: clear MSAL cache programmatically
      try { await instance.clearCache() } catch { /* best effort — fall through */ }
      sessionStorage.clear()
      localStorage.removeItem('xtract_return_url')
    }
    // logoutRedirect handles the navigation via postLogoutRedirectUri
  }, [instance])

  const value = useMemo(() => ({
    isAuthenticated,
    isLoading,
    user,
    accessToken,
    organizationName,
    sessionExpired,
    accountBlocked,
    login: handleLogin,
    logout: handleLogout,
    getAccessToken,
    clearSessionExpired,
  }), [isAuthenticated, isLoading, user, accessToken, organizationName, sessionExpired, accountBlocked, handleLogin, handleLogout, getAccessToken, clearSessionExpired])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Main AuthProvider that chooses between dev and production
export function AuthProvider({ children }) {
  if (!isMsalConfigured()) {
    return <DevAuthProvider>{children}</DevAuthProvider>
  }
  return <MsalAuthProvider>{children}</MsalAuthProvider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
